import { Link as MaterialLink } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { getDateErrors } from '../../../util/util';
import { StyledSectionContainer } from '../../common';
import { FastForm } from '../../fastForm';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
	FORM_NAME,
} from '../../fastForm/data/constants';
import FieldLabel from '../../fields/FieldLabel';
import DefaultDatePicker from '../../form/DefaultDatePicker';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme) => ({
	grid: {
		paddingTop: '10px',
		paddingRight: '10px',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},
	linkWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginBottom: theme.spacing(1),
	},
	selectInput: {
		'& .MuiSvgIcon-root': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

/**
 * The policy detail page for an renewal.
 */
const PolicyDetails = (props: Props) => {
	const classes = useStyles();
	const { authData, renewalData } = props;

	const {
		renewalUpdateObj,
		setRenewalUpdateObj,
		productConfig,
		policy,
		setLoading,
		saveFastForm,
		fields: form,
		resourcesTimestamps,
	} = renewalData;

	const { policyConfiguration } = productConfig;

	const renewalEndDate = renewalUpdateObj.renewalEndTimestamp;
	const currPolicyEndDate = policy.effectiveContractEndTimestamp
		? new Date(parseInt(policy.effectiveContractEndTimestamp, 10))
		: null;
	const paymentScheduleName =
		renewalUpdateObj.newPaymentScheduleName || policy.paymentScheduleName;

	const [endDate, setEndDate] = useState<Date>(
		renewalEndDate ? new Date(parseInt(renewalEndDate, 10)) : null,
	);

	let minAllowedDate: number = parseInt(
		renewalUpdateObj.renewalStartTimestamp,
		10,
	);

	let overrideResourcesAsOfTimestamp =
		renewalUpdateObj.overrideResourcesAsOfTimestamp
			? renewalUpdateObj.overrideResourcesAsOfTimestamp
			: renewalData.renewalResponse.overrideResourcesAsOfTimestamp;
	if (renewalUpdateObj.resetOverrideResourcesAsOfTimestamp) {
		overrideResourcesAsOfTimestamp = null;
	}

	const minDate = new Date(minAllowedDate);
	const renewalEndDateErrors = getDateErrors(
		endDate,
		minDate,
		null,
		true,
		authData,
	);

	useEffect(() => {
		const source = axios.CancelToken.source();

		return () => {
			source.cancel();
		};
	}, []);

	useEffect(() => {
		// whenever renewalUpdateObj updates (on save, quote, accept, issue), set date fields to renewalUpdateObj dates
		setEndDate(renewalEndDate ? new Date(parseInt(renewalEndDate, 10)) : null);
	}, [renewalUpdateObj]);

	const renderForm = () => {
		const paymentScheduleOptions = getPaymentScheduleOptions();
		const defaultPaymentSchedule = paymentScheduleOptions.find(
			(p) => p.value === paymentScheduleName,
		) || { value: paymentScheduleName, label: paymentScheduleName };

		if (!paymentScheduleOptions) {
			return null;
		}

		const updateRenewalEndDate = (date: Date, value: string) => {
			const validDate = date && !isNaN(date.getTime());
			setEndDate(date);
			renewalUpdateObj.renewalEndTimestamp = validDate
				? `${date.getTime()}`
				: null;
			setRenewalUpdateObj(renewalUpdateObj);
		};

		const isEditable =
			authData.hasPermission('renewal.update') &&
			renewalUpdateObj.state === 'application';

		const updatePaymentSchedule = (option: SelectValue) => {
			const paymentSchedule = option.value;
			renewalUpdateObj.newPaymentScheduleName = paymentSchedule;
			setRenewalUpdateObj(renewalUpdateObj);
		};

		const updateOverrideResourcesTimestamp = (option: {
			value: string;
			label: string;
		}) => {
			const overrideResourcesTimestamp = option.value;
			if (
				!overrideResourcesTimestamp &&
				renewalData.renewalResponse.overrideResourcesAsOfTimestamp
			) {
				renewalUpdateObj.resetOverrideResourcesAsOfTimestamp = true;
				renewalUpdateObj.overrideResourcesAsOfTimestamp = null;
			} else {
				renewalUpdateObj.overrideResourcesAsOfTimestamp =
					overrideResourcesTimestamp;
			}
			setRenewalUpdateObj(renewalUpdateObj);
		};

		const hasPaymentPlanPluginEnabled =
			!!policyConfiguration?.plugins?.createInstallments?.enabled;

		return (
			<section>
				<Grid container alignContent='center' spacing={2}>
					<Grid item xs={12} alignSelf='flex-end' sx={{ textAlign: 'right' }}>
						{authData.hasPermission('auxData.fetch') && (
							<MaterialLink component={RouterLink} to='../auxData'>
								Aux Data
							</MaterialLink>
						)}
					</Grid>
					<Grid item xs={12}>
						<DefaultDatePicker
							id='current-policy-end'
							label={`Current Policy End`}
							disabled
							showCalendar
							style={{ marginRight: '10px', flexGrow: 1 }}
							value={currPolicyEndDate}
							onChange={null}
						/>
						<DefaultDatePicker
							id='renewal-effective-date'
							label={`Renewal End Date`}
							minDate={minDate}
							maxDate={null}
							error={!renewalEndDateErrors.isValid}
							errorMessage={
								!renewalEndDateErrors.isValid
									? renewalEndDateErrors.message
									: null
							}
							value={endDate}
							required
							showCalendar
							disabled={!isEditable}
							style={{ marginRight: '20px', flexGrow: 1 }}
							onChange={updateRenewalEndDate}
						/>
						{isEditable && resourcesTimestamps?.length > 0 && (
							<FormControl style={{ marginRight: '20px', flexGrow: 1 }}>
								<FieldLabel title={'Override Resources Selection'} />
								<ReactSelect
									includeEmptyValue={true}
									className={isEditable ? classes.selectInput : ''}
									id='override-resources-ts-selector'
									value={{
										value: `${overrideResourcesAsOfTimestamp}`,
										label: overrideResourcesAsOfTimestamp
											? authData.getZonedFormattedDateString(
													overrideResourcesAsOfTimestamp,
													DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
											  )
											: null,
									}}
									options={resourcesTimestamps.map((ts) => ({
										value: `${ts}`,
										label: authData.getZonedFormattedDateString(
											`${ts}`,
											DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
										),
									}))}
									onChange={updateOverrideResourcesTimestamp}
									isDisabled={!isEditable}
								/>
							</FormControl>
						)}
					</Grid>
					<Grid item xs={12}>
						{paymentScheduleOptions.length > 0 && hasPaymentPlanPluginEnabled && (
							<Grid
								item
								xs={6}
								className={classes.grid}
								key={'newPaymentSchedule'}
							>
								<FormControl style={{ flexGrow: 1 }}>
									<FieldLabel title={'New Payment Schedule'} />
									<ReactSelect
										id='payment-schedule-selector'
										value={defaultPaymentSchedule}
										className={isEditable ? classes.selectInput : ''}
										options={paymentScheduleOptions}
										onChange={updatePaymentSchedule}
										isDisabled={renewalUpdateObj.state !== 'application'}
									/>
								</FormControl>
							</Grid>
						)}
					</Grid>
					<FastForm
						formName={FORM_NAME.RENEWAL_FORM}
						form={form} //created by formBuilder, attached to renewalData
						configVersion={productConfig.configVersion}
						authData={authData}
						productName={productConfig.name}
						disabled={!isEditable}
						save={saveFastForm}
						setLoading={setLoading}
						disableExternalCalls={true}
						effectiveDate={overrideResourcesAsOfTimestamp}
					/>
				</Grid>
			</section>
		);
	};

	const getPaymentScheduleOptions = () => {
		if (!productConfig) {
			return null;
		}

		const { paymentSchedules } = policyConfiguration;

		return paymentSchedules.map(
			(paymentSchedule) =>
				({
					value: paymentSchedule.name,
					label: paymentSchedule.displayName,
				} as any),
		);
	};

	return <StyledSectionContainer>{renderForm()}</StyledSectionContainer>;
};

export default withRenewalContext<Props>(PolicyDetails);
